import React, { useState } from "react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { CheckCircle2, Clock, Accessibility, Bell, Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ACCESSIBILITY_DATE, pad, useCountdown } from "@/lib/launch";

const TIER1 = ["Slack", "Google Workspace", "Microsoft Teams", "Stripe", "Intercom"];
const PHASE2 = ["Zoom", "Jira", "Asana", "Notion", "HubSpot", "Salesforce", "Zapier", "Monday.com"];
const LANGUAGES = ["English", "German", "Spanish", "Italian", "French"];

function AccessibilityCard() {
  const { days, hours, minutes, seconds } = useCountdown(ACCESSIBILITY_DATE);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const notify = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email.");
    setLoading(true);
    try {
      await api.post("/waitlist/notify", { email: email.trim(), topic: "accessibility-voiceover" });
      setDone(true);
      toast.success("You're on the accessibility notification list.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not add you. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#1A3A5C] bg-[#1A3A5C] text-white p-7 lg:p-8 col-span-2 md:col-span-3 lg:col-span-4 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="integration-coming-accessibility">
      <div className="lg:col-span-7">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-white/10 flex items-center justify-center">
            <Accessibility size={20} />
          </div>
          <div>
            <p className="font-display text-xl font-bold">Accessibility</p>
            <span className="text-xs uppercase tracking-[0.18em] text-[#7DE3B4] font-semibold">Coming soon · 1 January 2027</span>
          </div>
        </div>
        <p className="mt-5 text-sm text-[#E8ECF2]/85 leading-relaxed">
          Focus support should not depend on how you read a screen. We are building full voice-over into WYLO: the refocus
          card, the FACE timeline and every setting will be spoken aloud on request, navigable entirely by keyboard or
          screen reader, and designed with WCAG 2.2 AA as the floor rather than the ceiling.
        </p>
        <p className="mt-3 text-sm text-[#E8ECF2]/85 leading-relaxed">
          We are working with blind and low-vision testers, and with neurodivergent users who prefer to hear rather than
          read a refocus prompt. Voice-over launches on <strong className="text-white">1 January 2027</strong> in five languages, with more to follow.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {LANGUAGES.map((l) => (
            <span key={l} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs">
              <Languages size={11} className="text-[#7DE3B4]" /> {l}
            </span>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5 flex flex-col justify-between gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#E8ECF2]/70 font-semibold mb-3">Voice-over arrives in</p>
          <div className="grid grid-cols-4 gap-2" data-testid="accessibility-countdown">
            {[["Days", days], ["Hours", hours], ["Minutes", minutes], ["Seconds", seconds]].map(([label, v], i) => (
              <div key={label} className="rounded-md bg-white/10 text-center py-3">
                <p key={v} className={`font-display font-bold text-2xl tabular-nums ${i === 3 ? "animate-[cd-pulse_1s_ease-out]" : ""}`} data-testid={`a11y-cd-${label.toLowerCase()}`}>
                  {i === 0 ? v : pad(v)}
                </p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E8ECF2]/70">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {done ? (
          <p className="text-sm text-[#7DE3B4] flex items-center gap-2" data-testid="accessibility-notify-done">
            <CheckCircle2 size={16} /> We'll email you when voice-over goes live.
          </p>
        ) : (
          <form onSubmit={notify} className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="bg-white text-[#1A1A2E] border-white/20 h-11"
              data-testid="accessibility-notify-email"
            />
            <Button type="submit" disabled={loading} className="bg-white text-[#1A3A5C] hover:bg-[#E8ECF2] h-11 px-5 shrink-0" data-testid="accessibility-notify-submit">
              <Bell size={14} className="mr-2" /> {loading ? "Adding…" : "Notify me"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Integrations() {
  return (
    <MarketingLayout>
      <section className="pt-28 pb-12 max-w-5xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Integrations</p>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter text-[#1A1A2E] leading-[0.95]">
          WYLO fits into the stack you already use.
        </h1>
        <p className="mt-6 text-lg text-[#555568] max-w-2xl leading-relaxed">
          Connect the tools that already define your working day. WYLO uses these integrations to classify FACE events
          accurately - never to monitor message content.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <h2 className="font-display text-2xl text-[#1A1A2E] mb-6">Active at launch</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {TIER1.map((t) => (
            <div key={t} className="rounded-xl border border-[#E8ECF2] bg-white p-6 flex flex-col gap-3" data-testid={`integration-active-${t.toLowerCase().replace(/\s/g, "-")}`}>
              <CheckCircle2 className="text-[#1D9E75]" size={20} />
              <p className="font-display text-lg font-bold text-[#1A1A2E]">{t}</p>
              <span className="text-xs uppercase tracking-[0.18em] text-[#1D9E75] font-semibold">Active</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <h2 className="font-display text-2xl text-[#1A1A2E] mb-6">Coming soon</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {PHASE2.map((t) => (
            <div key={t} className="rounded-xl border border-[#E8ECF2] bg-white p-6 flex flex-col gap-3 opacity-80" data-testid={`integration-coming-${t.toLowerCase()}`}>
              <Clock className="text-[#BA7517]" size={20} />
              <p className="font-display text-lg font-bold text-[#1A1A2E]">{t}</p>
              <span className="text-xs uppercase tracking-[0.18em] text-[#BA7517] font-semibold">Coming soon</span>
            </div>
          ))}
          <AccessibilityCard />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-32">
        <RequestIntegration />
      </section>
    </MarketingLayout>
  );
}

function RequestIntegration() {
  const [form, setForm] = useState({ name: "", kind: "website", url: "", email: "", reason: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please tell us the name of the website or app.");
    setLoading(true);
    try {
      await api.post("/integrations/request", {
        name: form.name.trim(), kind: form.kind,
        url: form.url.trim() || null, email: form.email.trim() || null, reason: form.reason.trim() || null,
      });
      setDone(true);
      toast.success("Request received. Thank you!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your request. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="rounded-2xl border border-[#E8ECF2] bg-white p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="integration-request">
      <div className="lg:col-span-5">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-3">Missing something?</p>
        <h2 className="font-display text-3xl tracking-tighter text-[#1A1A2E]">Request a website or app.</h2>
        <p className="mt-4 text-sm text-[#555568] leading-relaxed">
          Tell us which websites and local apps you'd like WYLO to recognise in your hierarchy. Requests go straight to the
          team and shape what we build next. Leave an email if you'd like to hear when it lands.
        </p>
      </div>
      {done ? (
        <div className="lg:col-span-7 flex items-center gap-3 text-[#1D9E75]" data-testid="integration-request-done">
          <CheckCircle2 size={20} /> <p className="text-sm">Thanks - we've logged your request{form.email ? ` and will email ${form.email} when it's available` : ""}.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="req-name">Website or app name</Label>
            <Input id="req-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Figma, Linear, Xero" className="mt-1" data-testid="integration-request-name" />
          </div>
          <div>
            <Label>Type</Label>
            <div className="mt-1 inline-flex w-full rounded-md border border-[#E8ECF2] p-1" data-testid="integration-request-kind">
              {[["website", "Website"], ["app", "Local app"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, kind: v })}
                  className={`flex-1 text-sm py-1.5 rounded ${form.kind === v ? "bg-[#1A3A5C] text-white" : "text-[#555568] hover:text-[#1A1A2E]"}`}
                  data-testid={`integration-request-kind-${v}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="req-url">URL or download link (optional)</Label>
            <Input id="req-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://" className="mt-1" data-testid="integration-request-url" />
          </div>
          <div>
            <Label htmlFor="req-email">Your email (optional)</Label>
            <Input id="req-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="mt-1" data-testid="integration-request-email" />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="req-reason">Why it matters to your workflow (optional)</Label>
            <Textarea id="req-reason" rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" data-testid="integration-request-reason" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={loading} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-11 px-6" data-testid="integration-request-submit">
              {loading ? "Sending…" : "Request it"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
